export interface ShortLivedStorageCredentials {
  credentials: string
  bucket: string
  expiry: Date
  entryId: string
  prefix?: string
  action: 'read' | 'create'
  accountUrl?: string
}

interface ShortLivedStorageCredentialsResponse {
  credentials: string
  bucket: string
  expiry: string
  account_url?: string | null
}

export interface RefreshStorageCredentialsParams {
  rolodexUrl: string
  entryId: string
  prefix?: string
  action?: 'read' | 'create'
  authorizedAxios: {
    get: (url: string) => Promise<{
      data: ShortLivedStorageCredentialsResponse
    }>
  }
  currentCredentials?: ShortLivedStorageCredentials | null
}

/** Reuses unexpired credentials only for the same entry, prefix, and action. */
export async function refreshStorageCredentials({
  rolodexUrl,
  entryId,
  prefix,
  action = 'create',
  authorizedAxios,
  currentCredentials,
}: RefreshStorageCredentialsParams): Promise<ShortLivedStorageCredentials> {
  if (
    currentCredentials &&
    currentCredentials.entryId === entryId &&
    currentCredentials.prefix === prefix &&
    currentCredentials.action === action &&
    Date.now() < currentCredentials.expiry.getTime()
  ) {
    return currentCredentials
  }

  const query = new URLSearchParams({ action })
  if (prefix) query.set('prefix', prefix)
  const url = `${rolodexUrl}/api/v2/files/shortlivedcreds/${entryId}?${query}`
  const response = await authorizedAxios.get(url)
  const data = response.data

  if (!data.credentials || !data.bucket || !data.expiry) {
    throw new Error('Invalid credentials response')
  }
  const expiry = new Date(
    /(?:Z|[+-]\d{2}:\d{2})$/i.test(data.expiry)
      ? data.expiry
      : `${data.expiry}Z`
  )
  if (!Number.isFinite(expiry.getTime())) {
    throw new Error('Invalid credentials expiry')
  }

  return {
    credentials: data.credentials,
    bucket: data.bucket,
    expiry,
    entryId,
    prefix,
    action,
    accountUrl: data.account_url ?? undefined,
  }
}

export interface FileWithStorageCredentialsParams {
  fullPath: string
  credentials: ShortLivedStorageCredentials
}

export interface UploadFileWithStorageCredentialsParams extends FileWithStorageCredentialsParams {
  file: File | Blob
}

function getGcsUploadUrl(
  fullPath: string,
  credentials: ShortLivedStorageCredentials
): string {
  const sanitizedPath = encodeURIComponent(fullPath)
  const bucketName = credentials.bucket.startsWith('gs://')
    ? credentials.bucket.slice(5)
    : credentials.bucket

  return `https://storage.googleapis.com/upload/storage/v1/b/${bucketName}/o?uploadType=media&name=${sanitizedPath}`
}

function getAzureBlobUrl(
  fullPath: string,
  credentials: ShortLivedStorageCredentials
): string {
  const container = encodeURIComponent(credentials.bucket)
  const blobPath = fullPath.split('/').map(encodeURIComponent).join('/')

  const accountUrl = credentials.accountUrl?.replace(/\/$/, '')
  const path = blobPath ? `/${blobPath}` : ''
  const token = credentials.credentials.replace(/^\?/, '')
  return `${accountUrl}/${container}${path}?${token}`
}

/** Downloads a file using either GCS OAuth credentials or an Azure SAS token. */
export async function downloadFileWithStorageCredentials({
  fullPath,
  credentials,
}: FileWithStorageCredentialsParams): Promise<Blob> {
  const isAzure = credentials.accountUrl !== undefined
  const url = isAzure
    ? getAzureBlobUrl(fullPath, credentials)
    : `${getGcsObjectsUrl(credentials)}/${encodeURIComponent(fullPath)}?alt=media`
  const response = await fetch(url, {
    method: 'GET',
    headers: isAzure
      ? {}
      : { Authorization: `Bearer ${credentials.credentials}` },
  })
  if (!response.ok) {
    throw new Error(
      `Download failed: ${response.status} ${response.statusText}`
    )
  }
  return response.blob()
}

/** Uploads a file using either GCS OAuth credentials or an Azure SAS token. */
export async function uploadFileWithStorageCredentials({
  file,
  fullPath,
  credentials,
}: UploadFileWithStorageCredentialsParams): Promise<void> {
  const isAzure = credentials.accountUrl !== undefined
  const uploadUrl = isAzure
    ? getAzureBlobUrl(fullPath, credentials)
    : getGcsUploadUrl(fullPath, credentials)
  const headers: Record<string, string> = {
    'Content-Type': 'application/octet-stream',
  }

  if (isAzure) {
    headers['x-ms-blob-type'] = 'BlockBlob'
  } else {
    headers.Authorization = `Bearer ${credentials.credentials}`
  }

  const response = await fetch(uploadUrl, {
    method: isAzure ? 'PUT' : 'POST',
    headers,
    body: file,
  })

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`)
  }
}

export interface StorageFile {
  name: string
  size?: string
}

interface StorageObjectList {
  items?: StorageFile[]
  nextPageToken?: string
}

interface ListStorageFilesParams {
  prefix: string
  getCredentials: () => Promise<ShortLivedStorageCredentials>
}

const STORAGE_DELETE_CONCURRENCY = 25

function getGcsObjectsUrl(credentials: ShortLivedStorageCredentials): string {
  const bucket = credentials.bucket.replace(/^gs:\/\//, '')
  return `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucket)}/o`
}

function parseAzureFileList(xml: string): StorageObjectList {
  const document = new DOMParser().parseFromString(xml, 'application/xml')
  if (
    document.querySelector('parsererror') ||
    document.documentElement.tagName !== 'EnumerationResults'
  ) {
    throw new Error('Invalid Azure file listing response')
  }
  const items = Array.from(document.querySelectorAll('Blobs > Blob')).map(
    (blob) => {
      const nameElement = blob.querySelector('Name')
      const name = nameElement?.textContent
      if (!name) throw new Error('Missing Azure blob name')
      return {
        name:
          nameElement.getAttribute('Encoded') === 'true'
            ? decodeURIComponent(name)
            : name,
        size:
          blob.querySelector('Properties > Content-Length')?.textContent ??
          undefined,
      }
    }
  )
  return {
    items,
    nextPageToken:
      document.querySelector('NextMarker')?.textContent || undefined,
  }
}

async function listStorageFilePage(
  prefix: string,
  pageToken: string | undefined,
  credentials: ShortLivedStorageCredentials
): Promise<StorageObjectList> {
  const isAzure = credentials.accountUrl !== undefined
  const url = new URL(
    isAzure ? getAzureBlobUrl('', credentials) : getGcsObjectsUrl(credentials)
  )
  url.searchParams.set('prefix', prefix)
  if (isAzure) {
    url.searchParams.set('restype', 'container')
    url.searchParams.set('comp', 'list')
    if (pageToken) url.searchParams.set('marker', pageToken)
  } else {
    url.searchParams.set('fields', 'items(name,size),nextPageToken')
    if (pageToken) url.searchParams.set('pageToken', pageToken)
  }
  const response = await fetch(url.toString(), {
    headers: isAzure
      ? {}
      : { Authorization: `Bearer ${credentials.credentials}` },
  })
  if (!response.ok) {
    throw new Error(
      `File listing failed: ${response.status} ${response.statusText}`
    )
  }
  return isAzure ? parseAzureFileList(await response.text()) : response.json()
}

/** Lists every page of GCS objects or Azure blobs under an exact prefix. */
export async function listFilesWithStorageCredentials({
  prefix,
  getCredentials,
}: ListStorageFilesParams): Promise<StorageFile[]> {
  const files: StorageFile[] = []
  let pageToken: string | undefined
  do {
    const credentials = await getCredentials()
    const page = await listStorageFilePage(prefix, pageToken, credentials)
    for (const item of page.items ?? []) {
      if (item.name.startsWith(prefix)) files.push(item)
    }
    pageToken = page.nextPageToken
  } while (pageToken)
  return files
}

/** Deletes object paths, or all objects under each prefix when deleteDirectory is set. */
export async function deleteFilesWithStorageCredentials({
  paths,
  deleteDirectory = false,
  getCredentials,
}: {
  paths: ReadonlyArray<string>
  deleteDirectory?: boolean
  getCredentials: () => Promise<ShortLivedStorageCredentials>
}): Promise<{ deleted: number; failed: number }> {
  const fullPaths = deleteDirectory
    ? (
        await Promise.all(
          paths.map((prefix) =>
            listFilesWithStorageCredentials({ prefix, getCredentials })
          )
        )
      ).flatMap((files) => files.map((file) => file.name))
    : paths
  let deleted = 0
  for (
    let offset = 0;
    offset < fullPaths.length;
    offset += STORAGE_DELETE_CONCURRENCY
  ) {
    const credentials = await getCredentials()
    const isAzure = credentials.accountUrl !== undefined
    const results = await Promise.allSettled(
      fullPaths
        .slice(offset, offset + STORAGE_DELETE_CONCURRENCY)
        .map(async (path) => {
          const url = isAzure
            ? getAzureBlobUrl(path, credentials)
            : `${getGcsObjectsUrl(credentials)}/${encodeURIComponent(path)}`
          const response = await fetch(url, {
            method: 'DELETE',
            headers: isAzure
              ? { 'x-ms-delete-snapshots': 'include' }
              : { Authorization: `Bearer ${credentials.credentials}` },
          })
          if (!response.ok && response.status !== 404) {
            throw new Error(
              `File deletion failed: ${response.status} ${response.statusText}`
            )
          }
        })
    )
    deleted += results.filter((result) => result.status === 'fulfilled').length
  }
  return { deleted, failed: fullPaths.length - deleted }
}
